import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const renewSid = new URLSearchParams(window.location.search).get("renew");
if (renewSid) localStorage.setItem("sahayak_session", renewSid);

export function getChatSessionId() {
  let id = localStorage.getItem("sahayak_session");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("sahayak_session", id);
  }
  return id;
}

export const startChat = (bot, ref) =>
  axios.post(`${API}/chat/start`, { session_id: getChatSessionId(), bot, ref: ref || null });

export const sendMessage = (bot, message, requestId) =>
  axios.post(`${API}/chat/message`, {
    session_id: getChatSessionId(), bot, message,
    lang: localStorage.getItem("kash_lang") || "en",
    request_id: requestId,
  });

export const sendVoice = (bot, blob) => {
  const fd = new FormData();
  fd.append("session_id", getChatSessionId());
  fd.append("bot", bot);
  fd.append("lang", localStorage.getItem("kash_lang") || "en");
  fd.append("audio", blob, "voice.webm");
  return axios.post(`${API}/chat/voice`, fd);
};

export const listInvoices = () =>
  axios.get(`${API}/invoices/list/${getChatSessionId()}`);

export const getKhata = () =>
  axios.get(`${API}/khata/${getChatSessionId()}`);

export const settleBill = (invoiceId) =>
  axios.post(`${API}/khata/settle`, { invoice_id: invoiceId });

export const recordPayment = (invoiceId, amount) =>
  axios.post(`${API}/khata/payment`, { invoice_id: invoiceId, amount });

export const setKhataPhone = (customer, phone) =>
  axios.post(`${API}/khata/phone`, { session_id: getChatSessionId(), customer, phone });

export function getReferralLink() {
  return `${window.location.origin}/?ref=${getChatSessionId()}`;
}

export const answerCardUrl = (messageId) => `${API}/answers/${messageId}/card`;

export const messageAudioUrl = (messageId) => `${API}/messages/${messageId}/audio`;

export const createCheckout = (planId) =>
  axios.post(`${API}/payments/checkout`, {
    plan_id: planId,
    origin_url: window.location.origin,
    chat_session_id: getChatSessionId(),
  });

export const paymentStatus = (sessionId) =>
  axios.get(`${API}/payments/status/${sessionId}`);

export const invoicePdfUrl = (invoiceId) => `${API}/invoices/${invoiceId}/pdf`;

export function canShareFiles() {
  try {
    return !!(navigator.canShare && navigator.canShare({ files: [new File(["x"], "t.pdf", { type: "application/pdf" })] }));
  } catch {
    return false;
  }
}

export async function shareInvoicePdf(inv) {
  try {
    const res = await fetch(invoicePdfUrl(inv.id));
    if (!res.ok) return false;
    const blob = await res.blob();
    const file = new File([blob], `${inv.invoice_no || "bill"}.pdf`, { type: "application/pdf" });
    await navigator.share({ files: [file], title: `Bill ${inv.invoice_no}`, text: invoiceToWhatsappText(inv) });
    return true;
  } catch (e) {
    if (e && e.name === "AbortError") return true;
    console.error("pdf share failed", e);
    return false;
  }
}

export const getShop = () => axios.get(`${API}/shop/${getChatSessionId()}`);

export const shopLogoUrl = () => `${API}/shop/logo-file/${getChatSessionId()}`;

export const uploadShopLogo = (file) => {
  const fd = new FormData();
  fd.append("session_id", getChatSessionId());
  fd.append("file", file);
  return axios.post(`${API}/shop/logo`, fd);
};

export const deleteShopLogo = () => axios.delete(`${API}/shop/logo/${getChatSessionId()}`);

export const getInventory = () => axios.get(`${API}/inventory/${getChatSessionId()}`);
export const saveInventoryItems = (items) =>
  axios.post(`${API}/inventory/items`, { session_id: getChatSessionId(), items });
export const deleteInventoryItem = (itemId) =>
  axios.delete(`${API}/inventory/${getChatSessionId()}/${itemId}`);
export const inventoryTemplateUrl = () => `${API}/inventory-template`;
export const uploadInventoryFile = (file) => {
  const fd = new FormData();
  fd.append("session_id", getChatSessionId());
  fd.append("file", file);
  return axios.post(`${API}/inventory/upload`, fd);
};
export const parseInventoryText = (text) =>
  axios.post(`${API}/inventory/parse-text`, { session_id: getChatSessionId(), text });
export const purchaseInventory = (items) =>
  axios.post(`${API}/inventory/purchase`, { session_id: getChatSessionId(), items });
export const parseInventoryVoice = (blob) => {
  const fd = new FormData();
  fd.append("session_id", getChatSessionId());
  fd.append("audio", blob, "stock.webm");
  return axios.post(`${API}/inventory/parse-voice`, fd);
};

export const saveShop = (settings) =>
  axios.put(`${API}/shop`, { session_id: getChatSessionId(), ...settings });
export const sendLinkCode = (phone) =>
  axios.post(`${API}/link/send-code`, { session_id: getChatSessionId(), phone });
export const verifyLinkCode = (phone, code) =>
  axios.post(`${API}/link/verify`, { session_id: getChatSessionId(), phone, code });
export const loginSendCode = (phone) =>
  axios.post(`${API}/auth/phone/send-code`, { phone });
export const loginVerify = (phone, code) =>
  axios.post(`${API}/auth/phone/verify`, { phone, code });
export const getCustomers = () =>
  axios.get(`${API}/customers/${getChatSessionId()}`);
export const saveCustomer = (customer, phone) =>
  axios.post(`${API}/khata/phone`, { session_id: getChatSessionId(), customer, phone });
export const getCustomerBills = (customer) =>
  axios.get(`${API}/customers/${getChatSessionId()}/${encodeURIComponent(customer)}/bills`);
export const deleteCustomer = (customer) =>
  axios.delete(`${API}/customers/${getChatSessionId()}/${encodeURIComponent(customer)}`);
export const editInvoice = (invoiceId, payload) =>
  axios.put(`${API}/invoices/${invoiceId}`, payload);
export const cancelInvoice = (invoiceId) =>
  axios.post(`${API}/invoices/${invoiceId}/cancel`);
export const deleteMessage = (messageId) =>
  axios.delete(`${API}/messages/${getChatSessionId()}/${messageId}`);
export const clearChat = (bot) =>
  axios.post(`${API}/chat/clear`, { session_id: getChatSessionId(), bot });
export const listPurchases = () =>
  axios.get(`${API}/purchases/${getChatSessionId()}`);
export const editPurchase = (purchaseId, payload) =>
  axios.put(`${API}/purchases/${purchaseId}`, { session_id: getChatSessionId(), ...payload });
export const deletePurchase = (purchaseId) =>
  axios.delete(`${API}/purchases/${getChatSessionId()}/${purchaseId}`);
export const returnBill = (invoiceId, items) =>
  axios.post(`${API}/invoices/${invoiceId}/return`, { items });
export const getSupplierKhata = () =>
  axios.get(`${API}/supplier-khata/${getChatSessionId()}`);
export const paySupplier = (supplier, amount) =>
  axios.post(`${API}/supplier-khata/payment`, { session_id: getChatSessionId(), supplier, amount });

export const getAdminStats = (key) =>
  axios.get(`${API}/admin/stats`, { headers: { "X-Admin-Key": key } });
export const getAdminSubscriptions = (key) =>
  axios.get(`${API}/admin/subscriptions`, { headers: { "X-Admin-Key": key } });

export function invoiceToWhatsappText(inv) {
  const lines = [`*Bill ${inv.invoice_no}* | ${inv.date}`];
  if (inv.store_name) lines.unshift(`*${inv.store_name}*`);
  lines.push(`Customer: ${inv.customer}`, "");
  (inv.items || []).forEach((it) =>
    lines.push(`• ${it.name} ${it.qty}${it.unit ? " " + it.unit : ""} × ₹${it.rate} = ₹${Number(it.amount).toFixed(2)}`)
  );
  lines.push("");
  const disc = Number(inv.discount || 0);
  if (inv.gst_enabled !== false || disc > 0) {
    lines.push(`Subtotal: ₹${Number(inv.subtotal).toFixed(2)}`);
    if (disc > 0) lines.push(`Discount${inv.discount_percent ? ` (${inv.discount_percent}%)` : ""}: -₹${disc.toFixed(2)}`);
    if (inv.gst_enabled !== false) lines.push(`GST: ₹${Number(inv.gst_amount).toFixed(2)}`);
  }
  lines.push(`*TOTAL: ₹${Number(inv.total).toFixed(2)}*`);
  return lines.join("\n");
}

export async function payWithRazorpay(planId) {
  const { data } = await axios.post(`${API}/payments/razorpay/order`, {
    plan_id: planId,
    chat_session_id: getChatSessionId(),
  });
  return new Promise((resolve, reject) => {
    const rzp = new window.Razorpay({
      key: data.key_id,
      amount: data.amount,
      currency: data.currency,
      name: "KashAssist",
      description: data.description,
      order_id: data.order_id,
      theme: { color: "#006D5B" },
      handler: async (res) => {
        try {
          await axios.post(`${API}/payments/razorpay/verify`, {
            razorpay_order_id: res.razorpay_order_id,
            razorpay_payment_id: res.razorpay_payment_id,
            razorpay_signature: res.razorpay_signature,
          });
          window.location.href = `/payment/success?session_id=${data.order_id}`;
          resolve();
        } catch (e) {
          reject(e);
        }
      },
      modal: { ondismiss: () => reject(new Error("dismissed")) },
    });
    rzp.open();
  });
}
